/**
 *  @class
 *  @function FreeShippingProgressBar
 *
 *  Tiered cart progress bar: Cart -> Free Shipping -> Free Gift.
 *  The gift tier is optional; when data-gift-amount is absent the bar behaves
 *  like the original single free-shipping bar. State is derived entirely from
 *  data attributes rendered by snippets/free-shipping.liquid, so it stays
 *  correct across the theme's cart section re-renders (the element reconnects
 *  and re-runs updateStatus each time).
 */

if (!customElements.get('free-shipping-progress-bar')) {
  let currentPercent = 0; // Store the current percentage globally

  class FreeShippingProgressBar extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.updateStatus();
    }

    get rate() {
      return (window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
    }

    money(cents) {
      const format =
        (window.theme && window.theme.settings && (window.theme.settings.money_format || window.theme.settings.money_with_currency_format)) ||
        '${{amount}}';
      if (typeof formatMoney === 'function') return formatMoney(cents, format);
      return '$' + (cents / 100).toFixed(2);
    }

    updateStatus() {
      const cartTotal = parseInt(this.dataset.cartTotal, 10) || 0;
      const shipAmount = Math.round((parseInt(this.dataset.shippingAmount, 10) || 0) * this.rate);
      const hasGift = this.dataset.giftAmount != null && this.dataset.giftAmount !== '';
      const giftAmount = hasGift ? Math.round(parseInt(this.dataset.giftAmount, 10) * this.rate) : 0;

      const shipEligible = cartTotal >= shipAmount;
      const giftEligible = hasGift && cartTotal >= giftAmount;

      // Active message + remaining amount.
      let message;
      if (!shipEligible) {
        message = this.withAmount(this.dataset.shipMessage, shipAmount - cartTotal);
      } else if (hasGift && !giftEligible) {
        message = this.withAmount(this.dataset.giftMessage, giftAmount - cartTotal);
      } else if (giftEligible) {
        message = this.dataset.giftUnlockedMessage;
      } else {
        message = this.dataset.eligibilityMessage;
      }
      const info = this.querySelector('.free-shipping__info-text');
      if (info && message != null) info.innerHTML = message;

      // Node completion states.
      this.setNode('ship', shipEligible);
      if (hasGift) this.setNode('gift', giftEligible);

      // Segment-based fill so the green reaches each evenly-spaced node exactly
      // when its threshold is hit: 0->ship maps to 0-50%, ship->gift to 50-100%.
      let progress;
      if (hasGift) {
        if (cartTotal <= shipAmount) {
          progress = shipAmount > 0 ? (cartTotal / shipAmount) * 0.5 : 0;
        } else {
          const span = giftAmount - shipAmount;
          const seg = span > 0 ? Math.min((cartTotal - shipAmount) / span, 1) : 1;
          progress = 0.5 + seg * 0.5;
        }
      } else {
        progress = shipAmount > 0 ? Math.min(cartTotal / shipAmount, 1) : 1;
      }
      this.animateProgressBar(progress);
    }

    withAmount(template, remainingCents) {
      const amount = '<strong>' + this.money(Math.max(remainingCents, 0)) + '</strong>';
      return (template || '').replace('{{ amount }}', amount).replace('{{amount}}', amount);
    }

    setNode(tier, complete) {
      const node = this.querySelector('.gwp-bar__node[data-tier="' + tier + '"]');
      if (node) node.classList.toggle('is-complete', !!complete);
    }

    animateProgressBar(progressPercentage) {
      const targetPercent = progressPercentage * 100;
      const startPercent = currentPercent;
      const duration = 200;
      const startTime = performance.now();

      const ease = (t) => t * (2 - t);

      const animate = (time) => {
        const elapsed = time - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = ease(progress);

        currentPercent = startPercent + (targetPercent - startPercent) * easedProgress;
        this.style.setProperty('--percent', `${currentPercent}%`);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          currentPercent = targetPercent;
          this.style.setProperty('--percent', `${currentPercent}%`);
        }
      };

      requestAnimationFrame(animate);
    }
  }

  customElements.define('free-shipping-progress-bar', FreeShippingProgressBar);
}
